/**
 * HVACSummary - Summary panel showing HVAC system totals and analysis
 */

import React, { useMemo } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import {
  calculateRoomHeatingLoad,
  calculateRoomCoolingLoad,
  formatLoad,
  btuToTons,
  getRecommendedSize,
} from '../../utils/loadCalculations';

/**
 * Calculate duct length in linear feet from duct points
 */
function calculateDuctLength(duct) {
  if (!duct?.points || duct.points.length < 2) return 0;
  let totalLength = 0;
  for (let i = 0; i < duct.points.length - 1; i++) {
    const p1 = duct.points[i];
    const p2 = duct.points[i + 1];
    const length = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
    totalLength += length;
  }
  // Convert from pixels to feet (40 pixels = 1 foot)
  return totalLength / 40;
}

/**
 * Calculate room area from points
 */
function calculateRoomArea(room) {
  const points = room?.points || room?.vertices;
  if (!points || points.length < 3) return 0;
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  area = Math.abs(area) / 2;
  return Math.round(area / (40 * 40)); // Convert to sq ft
}

/**
 * Check if a point is inside a room polygon
 */
function isPointInRoom(x, y, room) {
  const points = room?.points || room?.vertices;
  if (!points || points.length < 3) return false;

  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

export default function HVACSummary({
  activeFloor,
  designConditions,
  onClose,
  isMobile = false,
}) {
  // Calculate all summary data
  const summary = useMemo(() => {
    const ducts = activeFloor?.ducts || [];
    const vents = activeFloor?.vents || [];
    const equipment = activeFloor?.equipment || [];
    const rooms = activeFloor?.rooms || [];
    const roomData = activeFloor?.roomData || [];
    const fittings = activeFloor?.fittings || [];

    // Ductwork calculations
    const supplyDucts = ducts.filter(d => d.type === 'supply');
    const returnDucts = ducts.filter(d => d.type === 'return');
    const supplyDuctLength = supplyDucts.reduce((sum, d) => sum + calculateDuctLength(d), 0);
    const returnDuctLength = returnDucts.reduce((sum, d) => sum + calculateDuctLength(d), 0);

    // Vent calculations
    const supplyVents = vents.filter(v => v.type?.includes('supply'));
    const returnVents = vents.filter(v => v.type?.includes('return'));
    const totalSupplyCFM = supplyVents.reduce((sum, v) => sum + (v.cfm || 0), 0);
    const totalReturnCFM = returnVents.reduce((sum, v) => sum + (v.cfm || 0), 0);

    // Room calculations
    const conditionedRooms = rooms.filter(r => {
      const data = roomData.find(rd => rd.roomId === r.id);
      return !data || data.conditioned !== 'no';
    });
    const totalConditionedArea = conditionedRooms.reduce((sum, r) => {
      const data = roomData.find(rd => rd.roomId === r.id);
      return sum + (data?.floorArea || calculateRoomArea(r));
    }, 0);

    // Room breakdown with vents
    const roomBreakdown = rooms.map(room => {
      const data = roomData.find(rd => rd.roomId === room.id);
      const area = data?.floorArea || calculateRoomArea(room);
      const ceilingHeight = data?.ceilingHeight || 8;

      // Find vents in this room
      const roomSupplyVents = supplyVents.filter(v => isPointInRoom(v.x, v.y, room));
      const roomReturnVents = returnVents.filter(v => isPointInRoom(v.x, v.y, room));

      // Window calculations from room data
      const windowCount = data?.windows?.length || 0;
      const windowArea = data?.windows?.reduce((sum, w) => sum + (w.width * w.height / 144), 0) || 0;

      return {
        id: room.id,
        name: data?.name || room.name || 'Unnamed Room',
        area,
        ceilingHeight,
        windowCount,
        windowArea: Math.round(windowArea * 10) / 10,
        supplyVents: roomSupplyVents.length,
        returnVents: roomReturnVents.length,
        supplyCFM: roomSupplyVents.reduce((sum, v) => sum + (v.cfm || 0), 0),
        returnCFM: roomReturnVents.reduce((sum, v) => sum + (v.cfm || 0), 0),
        conditioned: data?.conditioned || 'yes',
      };
    });

    // Equipment list
    const equipmentList = equipment.map(e => ({
      id: e.id,
      type: e.type,
      size: e.size,
      label: getEquipmentLabel(e),
    }));

    // Calculate heating/cooling loads
    let totalHeatingLoad = 0;
    let totalCoolingLoad = 0;
    let totalSensibleLoad = 0;
    let totalLatentLoad = 0;

    if (designConditions) {
      roomBreakdown.forEach(room => {
        if (room.conditioned !== 'no') {
          // Find full room data for this room
          const fullRoomData = roomData.find(rd => rd.roomId === room.id);

          // Create room data object for calculation
          const calcRoomData = fullRoomData || {
            name: room.name,
            conditioned: 'yes',
            floorArea: room.area,
            ceilingHeight: room.ceilingHeight || 8,
            ceilingType: 'below-vented-attic',
            windows: [],
            exteriorWalls: [],
            floorType: 'slab',
            doors: [],
          };

          const heating = calculateRoomHeatingLoad(calcRoomData, designConditions);
          const cooling = calculateRoomCoolingLoad(calcRoomData, designConditions);

          room.heatingLoad = heating.total;
          room.coolingLoad = cooling.total;
          room.sensibleLoad = cooling.sensible;
          room.latentLoad = cooling.latent;

          totalHeatingLoad += heating.total;
          totalCoolingLoad += cooling.total;
          totalSensibleLoad += cooling.sensible;
          totalLatentLoad += cooling.latent;
        }
      });
    }

    // Generate warnings
    const warnings = [];

    // Rooms without supply vents
    roomBreakdown.forEach(room => {
      if (room.conditioned !== 'no') {
        if (room.supplyVents === 0) {
          warnings.push({
            type: 'error',
            message: `${room.name} has no supply vent`,
          });
        }
        if (room.returnVents === 0 && room.area > 100) {
          warnings.push({
            type: 'warning',
            message: `${room.name} has no return path`,
          });
        }
        // Large room with small CFM
        if (room.area > 200 && room.supplyCFM > 0 && room.supplyCFM < room.area * 0.5) {
          warnings.push({
            type: 'warning',
            message: `${room.name} may need more airflow (${room.supplyCFM} CFM for ${room.area} sq ft)`,
          });
        }
      }
    });

    // Equipment not connected
    if (equipment.length > 0 && ducts.length === 0) {
      warnings.push({
        type: 'warning',
        message: 'Equipment placed but no ductwork connected',
      });
    }

    // CFM imbalance
    if (totalSupplyCFM > 0 && totalReturnCFM > 0) {
      const imbalance = Math.abs(totalSupplyCFM - totalReturnCFM) / totalSupplyCFM;
      if (imbalance > 0.2) {
        warnings.push({
          type: 'warning',
          message: `Supply/return CFM imbalance: ${totalSupplyCFM} vs ${totalReturnCFM}`,
        });
      }
    }

    return {
      // System overview
      totalConditionedArea,
      totalRooms: rooms.length,
      conditionedRooms: conditionedRooms.length,
      equipmentCount: equipment.length,
      fittingCount: fittings.length,

      // Ductwork
      supplyDuctLength: Math.round(supplyDuctLength),
      returnDuctLength: Math.round(returnDuctLength),
      totalDuctLength: Math.round(supplyDuctLength + returnDuctLength),

      // Airflow
      supplyVentCount: supplyVents.length,
      returnVentCount: returnVents.length,
      totalSupplyCFM,
      totalReturnCFM,

      // Details
      equipmentList,
      roomBreakdown,
      warnings,

      // Load calculations
      totalHeatingLoad,
      totalCoolingLoad,
      totalSensibleLoad,
      totalLatentLoad,
      coolingTons: btuToTons(totalCoolingLoad),
      recommendedCooling: getRecommendedSize(totalCoolingLoad, 'cooling'),
      recommendedHeating: getRecommendedSize(totalHeatingLoad, 'heating'),
    };
  }, [activeFloor, designConditions]);

  return (
    <FloatingPanel
      title="HVAC Summary"
      onClose={onClose}
      position={isMobile ? 'bottom' : 'right'}
      width={isMobile ? '100%' : 340}
    >
      {/* Warnings */}
      {summary.warnings.length > 0 && (
        <PanelSection title={`Warnings (${summary.warnings.length})`} defaultExpanded>
          <div style={styles.warningsList}>
            {summary.warnings.map((warning, idx) => (
              <div
                key={idx}
                style={{
                  ...styles.warningItem,
                  borderLeftColor: warning.type === 'error' ? '#f44336' : '#ff9800',
                }}
              >
                <span style={styles.warningIcon}>
                  {warning.type === 'error' ? '!' : '?'}
                </span>
                {warning.message}
              </div>
            ))}
          </div>
        </PanelSection>
      )}

      {/* System Overview */}
      <PanelSection title="System Overview" defaultExpanded>
        <div style={styles.statsGrid}>
          <StatItem label="Conditioned Area" value={`${summary.totalConditionedArea} sq ft`} />
          <StatItem label="Rooms" value={`${summary.conditionedRooms} / ${summary.totalRooms}`} />
          <StatItem label="Equipment" value={summary.equipmentCount} />
          <StatItem label="Fittings" value={summary.fittingCount} />
        </div>
      </PanelSection>

      {/* Load Calculations */}
      {designConditions && (summary.totalHeatingLoad > 0 || summary.totalCoolingLoad > 0) && (
        <PanelSection title="Load Calculations" defaultExpanded>
          <div style={styles.loadSection}>
            {/* Heating */}
            <div style={styles.loadCard}>
              <div style={styles.loadHeader}>
                <span style={styles.loadIcon}>🔥</span>
                <span style={styles.loadTitle}>Heating Load</span>
              </div>
              <div style={styles.loadValue}>
                {formatLoad(summary.totalHeatingLoad)} BTU/hr
              </div>
              {summary.recommendedHeating && (
                <div style={styles.loadRecommend}>
                  Recommended: {summary.recommendedHeating}
                </div>
              )}
            </div>

            {/* Cooling */}
            <div style={styles.loadCard}>
              <div style={styles.loadHeader}>
                <span style={styles.loadIcon}>❄️</span>
                <span style={styles.loadTitle}>Cooling Load</span>
              </div>
              <div style={styles.loadValue}>
                {formatLoad(summary.totalCoolingLoad)} BTU/hr
              </div>
              <div style={styles.loadSubValue}>
                ({summary.coolingTons} tons)
              </div>
              {summary.recommendedCooling && (
                <div style={styles.loadRecommend}>
                  Recommended: {summary.recommendedCooling}
                </div>
              )}
            </div>

            {/* Breakdown */}
            <div style={styles.loadBreakdown}>
              <div style={styles.loadBreakdownItem}>
                <span style={styles.loadBreakdownLabel}>Sensible:</span>
                <span style={styles.loadBreakdownValue}>{formatLoad(summary.totalSensibleLoad)}</span>
              </div>
              <div style={styles.loadBreakdownItem}>
                <span style={styles.loadBreakdownLabel}>Latent:</span>
                <span style={styles.loadBreakdownValue}>{formatLoad(summary.totalLatentLoad)}</span>
              </div>
            </div>
          </div>
        </PanelSection>
      )}

      {/* Ductwork Summary */}
      <PanelSection title="Ductwork">
        <div style={styles.statsGrid}>
          <StatItem label="Supply Duct" value={`${summary.supplyDuctLength} ft`} color="#e65100" />
          <StatItem label="Return Duct" value={`${summary.returnDuctLength} ft`} color="#1565c0" />
          <StatItem label="Total Ductwork" value={`${summary.totalDuctLength} ft`} />
        </div>
      </PanelSection>

      {/* Airflow Summary */}
      <PanelSection title="Airflow">
        <div style={styles.statsGrid}>
          <StatItem label="Supply Registers" value={summary.supplyVentCount} color="#e65100" />
          <StatItem label="Return Grilles" value={summary.returnVentCount} color="#1565c0" />
          {summary.totalSupplyCFM > 0 && (
            <StatItem label="Total Supply CFM" value={summary.totalSupplyCFM} />
          )}
          {summary.totalReturnCFM > 0 && (
            <StatItem label="Total Return CFM" value={summary.totalReturnCFM} />
          )}
        </div>
      </PanelSection>

      {/* Equipment List */}
      {summary.equipmentList.length > 0 && (
        <PanelSection title="Equipment">
          <div style={styles.equipmentList}>
            {summary.equipmentList.map(equip => (
              <div key={equip.id} style={styles.equipmentItem}>
                <span style={styles.equipmentIcon}>*</span>
                {equip.label}
              </div>
            ))}
          </div>
        </PanelSection>
      )}

      {/* Room Breakdown */}
      {summary.roomBreakdown.length > 0 && (
        <PanelSection title="Room Breakdown">
          <div style={styles.roomTable}>
            <div style={styles.roomTableHeader}>
              <span style={styles.roomNameCol}>Room</span>
              <span style={styles.roomStatCol}>Sq Ft</span>
              <span style={styles.roomStatCol}>Win</span>
              <span style={styles.roomStatCol}>Sup</span>
              <span style={styles.roomStatCol}>Ret</span>
            </div>
            {summary.roomBreakdown.map(room => (
              <div
                key={room.id}
                style={{
                  ...styles.roomTableRow,
                  opacity: room.conditioned === 'no' ? 0.5 : 1,
                }}
              >
                <span style={styles.roomNameCol} title={room.name}>
                  {room.name.length > 12 ? room.name.slice(0, 10) + '...' : room.name}
                </span>
                <span style={styles.roomStatCol}>{room.area}</span>
                <span style={styles.roomStatCol}>{room.windowCount}</span>
                <span style={{
                  ...styles.roomStatCol,
                  color: room.supplyVents === 0 && room.conditioned !== 'no' ? '#f44336' : 'inherit',
                }}>
                  {room.supplyVents}
                </span>
                <span style={{
                  ...styles.roomStatCol,
                  color: room.returnVents === 0 && room.area > 100 && room.conditioned !== 'no' ? '#ff9800' : 'inherit',
                }}>
                  {room.returnVents}
                </span>
              </div>
            ))}
          </div>
        </PanelSection>
      )}

      {/* Empty state */}
      {summary.totalRooms === 0 && summary.equipmentCount === 0 && (
        <div style={styles.emptyState}>
          <p>No HVAC elements found.</p>
          <p style={styles.emptyHint}>
            Draw rooms, place equipment, ducts, and vents to see the summary.
          </p>
        </div>
      )}
    </FloatingPanel>
  );
}

// Stat item component
function StatItem({ label, value, color }) {
  return (
    <div style={styles.statItem}>
      <span style={styles.statLabel}>{label}</span>
      <span style={{ ...styles.statValue, color: color || '#fff' }}>{value}</span>
    </div>
  );
}

// Helper to get equipment label
function getEquipmentLabel(equipment) {
  const typeLabels = {
    'furnace-gas': 'Gas Furnace',
    'furnace-electric': 'Electric Furnace',
    'ac-condenser': 'AC Condenser',
    'air-handler': 'Air Handler',
    'heat-pump': 'Heat Pump',
    'mini-split-indoor': 'Mini Split Head',
    'mini-split-outdoor': 'Mini Split Outdoor',
    'thermostat': 'Thermostat',
  };

  const sizeLabels = {
    '40K': '40,000 BTU',
    '60K': '60,000 BTU',
    '80K': '80,000 BTU',
    '100K': '100,000 BTU',
    '1.5': '1.5 Ton',
    '2': '2 Ton',
    '2.5': '2.5 Ton',
    '3': '3 Ton',
    '3.5': '3.5 Ton',
    '4': '4 Ton',
    '5': '5 Ton',
    '9K': '9,000 BTU',
    '12K': '12,000 BTU',
    '18K': '18,000 BTU',
    '24K': '24,000 BTU',
  };

  const typeName = typeLabels[equipment.type] || equipment.type;
  const sizeName = sizeLabels[equipment.size] || equipment.size || '';

  return sizeName ? `${typeName} ${sizeName}` : typeName;
}

const styles = {
  warningsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  warningItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 10px',
    background: 'rgba(255,152,0,0.1)',
    borderLeft: '3px solid #ff9800',
    borderRadius: '0 4px 4px 0',
    fontSize: '12px',
    color: '#ffcc80',
  },
  warningIcon: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'rgba(255,152,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '4px',
  },
  statLabel: {
    fontSize: '10px',
    color: '#888',
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  equipmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  equipmentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#e0e0e0',
  },
  equipmentIcon: {
    color: '#66bb6a',
  },
  roomTable: {
    fontSize: '11px',
  },
  roomTableHeader: {
    display: 'flex',
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '4px 4px 0 0',
    fontWeight: 'bold',
    color: '#aaa',
  },
  roomTableRow: {
    display: 'flex',
    padding: '6px 8px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    color: '#e0e0e0',
  },
  roomNameCol: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  roomStatCol: {
    width: '40px',
    textAlign: 'center',
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#888',
  },
  emptyHint: {
    fontSize: '12px',
    marginTop: '8px',
    color: '#666',
  },
  loadSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  loadCard: {
    padding: '12px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  loadHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  loadIcon: {
    fontSize: '16px',
  },
  loadTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#a0b0c0',
  },
  loadValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#fff',
  },
  loadSubValue: {
    fontSize: '12px',
    color: '#8090a0',
    marginTop: '2px',
  },
  loadRecommend: {
    fontSize: '11px',
    color: '#4caf50',
    marginTop: '6px',
    padding: '4px 8px',
    background: 'rgba(76,175,80,0.1)',
    borderRadius: '4px',
  },
  loadBreakdown: {
    display: 'flex',
    gap: '16px',
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '6px',
  },
  loadBreakdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  loadBreakdownLabel: {
    fontSize: '11px',
    color: '#6080a0',
  },
  loadBreakdownValue: {
    fontSize: '12px',
    color: '#a0c0e0',
    fontWeight: '600',
  },
};
